import { firstValueSeen } from "../lib/completion-analytics";
import {
	rowPath,
	rowFacts,
	seatLayoutFor,
	sectionFromPath,
	venueBySlug,
} from "@anjabom/core";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { TipList, type PublicTip } from "../components/TipList";
export function RowPage() {
	const { venueSlug, spaceId, floorId, sectionSlug, rowLabel } = useParams();
	const venue = venueBySlug(venueSlug ?? ""),
		layout = venue ? seatLayoutFor(venue.id, spaceId ?? "") : null;
	const section = layout
		? sectionFromPath(layout, floorId ?? "", sectionSlug ?? "")
		: null;
	const row = section?.rows?.find((row) => row.label === rowLabel);
	const [tips, setTips] = useState<PublicTip[] | null>(null),
		[error, setError] = useState(""),
		[retry, setRetry] = useState(0);
	useEffect(() => {
		if (row) firstValueSeen();
	}, [row]);
	const key =
		layout && section && row
			? [layout.venueId, layout.layoutVersionId, section.label, row.label].join(
					":",
				)
			: null;
	useEffect(() => {
		if (!key) return;
		const controller = new AbortController();
		setError("");
		setTips(null);
		fetch(`/api/rows/${encodeURIComponent(key)}/tips`, {
			signal: controller.signal,
		})
			.then(async (r) => {
				if (!r.ok) throw new Error();
				setTips(((await r.json()) as { tips: PublicTip[] }).tips);
			})
			.catch(() => {
				if (!controller.signal.aborted)
					setError("한 줄을 불러오지 못했어요. 연결을 확인해 주세요.");
			});
		return () => controller.abort();
	}, [key, retry]);
	if (!layout || !venue || !section || !row)
		return (
			<section className="page stack">
				<h1>이 열을 확인하지 못했어요.</h1>
				<Link to="/start">좌석 다시 찾기</Link>
			</section>
		);
	const index = section.rows?.indexOf(row) ?? 0;
	const facts = rowFacts(layout, section.id, row.label);
	return (
		<section className="page narrow stack-lg">
			<h1>
				{venue.name} {venue.spaces.find((s) => s.id === layout.spaceId)?.name}{" "}
				{section.label} {row.label}열 시야
			</h1>
			<ul>
				{facts.lines.map((line) => (
					<li key={line}>{line}</li>
				))}
			</ul>
			<p className="micro">
				{layout.source.label} · {layout.source.checkedAt} 확인
			</p>
			<div className="row-seat-picker">
				{row.seats.map((n) => (
					<Link
						className="button seat-number"
						key={n}
						to={`/seat/${encodeURIComponent(`${key}:${n}`)}`}
					>
						{n}번
						{tips?.some((tip) => tip.seatNumber === n) ? (
							<span aria-label="한 줄 있어요">●</span>
						) : null}
					</Link>
				))}
			</div>
			<nav className="inline" aria-label="앞뒤 열">
				{[index - 1, index + 1].map((i) => {
					const next = section.rows?.[i];
					return next ? (
						<Link
							className="button"
							key={i}
							to={rowPath(layout, venue.slug, section.id, next.label) ?? "#"}
						>
							{next.label}열 보기
						</Link>
					) : null;
				})}
			</nav>
			<h2>이 열에 앉아본 사람 한 줄</h2>
			{error ? (
				<p role="alert">
					{error}{" "}
					<button type="button" onClick={() => setRetry(retry + 1)}>
						다시 불러오기
					</button>
				</p>
			) : tips ? (
				<TipList tips={tips} />
			) : (
				<p role="status">한 줄을 불러오고 있어요.</p>
			)}
			<Link
				className="button button-primary"
				to={`/seat/${encodeURIComponent(`${key}:${row.seats[0]}`)}`}
			>
				궁금한 자리 확인하고 한 줄 남기기
			</Link>
		</section>
	);
}
